import { useEffect, useRef, useState } from "react";
import { useCuentaAtlas } from "../hooks/useCuentaAtlas";
import { SoloElDueno } from "../components/SoloElDueno";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  X,
  Lock,
  ChevronDown,
  Tag,
  ImagePlus,
  Gift,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { atlasBackendFetch } from "../lib/atlasBackend";
import {
  fetchStripeCheckoutUrl,
  fetchStripeBillingPortalUrl,
  fetchRebillBillingStatus,
} from "../lib/paymentServer";
import { useUserGroup } from "../hooks/useUserGroup";
import { entryStudioPath } from "../lib/userGroups";
import {
  miInvitacionReferido,
  type MiInvitacionReferido,
} from "../lib/referidos";
import RebillCardPayment, {
  type RebillCheckoutResult,
  type RebillPrefetchedCtx,
} from "../components/RebillCardPayment";
import { ensureRebillSDK } from "../lib/rebill";
import RebillChangePlanConfirm from "../components/RebillChangePlanConfirm";
import {
  plans,
  paisFlags,
  currencyForPais,
  getPlanPriceForPais,
  formatPriceForPais,
  getPlanById,
  PLAN_UPGRADE_INCREMENTS,
  cambioDePlanAdmiteDescuento,
  destinoPostPago,
} from "../lib/checkoutPlans";
import { parametrosDePlan, trackMeta } from "../lib/metaPixel";

// ─── One-click de cambio de plan: APAGADO (2026-08-07) ──────────────────────
// En `false` NADIE ve "Confirmar cambio de plan": todos los clientes AR pasan por
// el checkout embebido y cargan la tarjeta, tengan o no una guardada en Rebill.
//
// Por qué se apagó: el one-click cobra el precio del `pln_` de Rebill y no aplica
// el cupón invisible del 30% del primer mes, así que el catálogo mostraba
// $279.999 y el cobro salía a $399.999. El checkout embebido, en cambio, sí lo
// aplica (78 cobros aprobados en 67 empresas, todos con el descuento correcto).
// Apagarlo no le saca nada a nadie: en 5 semanas el one-click aprobó 2 pagos, los
// dos de la cuenta "Prueba".
//
// Para reactivarlo (p. ej. si se discontinúa el descuento) alcanza con poner
// `true` acá: el resto del camino quedó implementado y esperando —
// `RebillChangePlanConfirm` ya manda `cupon_id` y consume `amountCharged` /
// `couponApplied`, y `Checkout` no lo pinta hasta resolver el cupón. Antes de
// prenderlo, verificar que el payment server aplique el cupón y que guarde el
// teléfono en el customer de Rebill (sin eso el cobro se rechaza igual).
// Ver ADR-2026-08-07-apagamos-el-one-click, ADR-2026-08-07-cupon-en-el-one-click
// y ADR-2026-08-07-telefono-no-se-guarda-el-one-click-no-aplica-a-nadie.
const ONE_CLICK_HABILITADO: boolean = false;

const CheckoutDelDueno = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDevTester: userGroupDevTester, grupo: userGroupGrupo } =
    useUserGroup();

  const planId = (location.state as any)?.planId as string | undefined;
  const pais = ((location.state as any)?.pais as string) || "Argentina";
  const initialCoupon = (location.state as any)?.coupon as
    | {
        id: string;
        codigo: string;
        multiplicador: number;
        es_invisible?: boolean;
      }
    | undefined;
  const currentPlan =
    ((location.state as any)?.currentPlan as string) || "gratis";

  // Plan hierarchy for determining upgrades
  const planOrder: Record<string, number> = {
    gratis: 0,
    emprendimiento: 1,
    negocio: 2,
    enterprise: 3,
    enterprise1: 3.1,
    enterprise2: 3.2,
    enterprise3: 3.3,
    experto: 4,
  };
  const isUpgrade = planId
    ? planOrder[planId] > (planOrder[currentPlan] ?? 0)
    : false;

  // Flags/moneda/precios viven en `lib/checkoutPlans.ts` desde ADR-592, para
  // que el alta por link use EXACTAMENTE los mismos valores. La extracción se
  // verificó comparando vieja vs nueva en las 224 combinaciones posibles
  // (16 países × 7 planes × precio+formato): sin diferencias.
  // Solo `isArgentina` se sigue usando acá (elige proveedor: Rebill vs Stripe,
  // y el copy/logo del método de pago). Los otros flags ya solo los necesitan
  // las funciones de precio, que ahora viven en el módulo compartido.
  const { isArgentina } = paisFlags(pais);
  const currencyCode = currencyForPais(pais);
  const providerName = isArgentina ? "Mercado Pago" : "Stripe";
  const providerLogoUrl = isArgentina
    ? "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//mercado-pago-logo.png"
    : "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/stripe-logotipo.png";
  const providerTermsUrl = isArgentina
    ? "https://www.mercadopago.com.ar/ayuda/terminos-y-condiciones_299"
    : "https://stripe.com/legal";

  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Error inline del cobro de Argentina (rechazo del primer cobro / fallo).
  const [cardError, setCardError] = useState<string | null>(null);
  // Detectamos el viewport para montar el CardPayment Brick UNA sola vez: los
  // layouts mobile y desktop conviven en el DOM (se ocultan por CSS), y dos
  // Bricks montados a la vez chocarían (mismo container del SDK de MP).
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 768px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const [showFeatures, setShowFeatures] = useState<boolean>(false);
  const [showMpEmailTutorial, setShowMpEmailTutorial] =
    useState<boolean>(false);
  const [mpEmailTutorialStep, setMpEmailTutorialStep] = useState<1 | 2>(1);

  // Estado de pago exitoso (AR/Rebill): al confirmarse, el panel izquierdo (desktop)
  // / top (mobile) muestra un mensaje de bienvenida en vez del detalle del plan.
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  // Nombre para la bienvenida: `name_short` + `name_sex_male` (bienvenido/a).
  const [welcomeInfo, setWelcomeInfo] = useState<{
    name: string;
    sexMale: boolean | null;
  } | null>(null);
  // Cambio de plan one-click: si la empresa YA está en Rebill (con tarjeta
  // guardada), mostramos "Confirmar cambio" en vez del checkout embebido.
  const [userId, setUserId] = useState<string | null>(null);
  const [isRebillCustomer, setIsRebillCustomer] = useState<boolean>(false);
  // Terminación de la tarjeta guardada en Rebill (del último pago Rebill, si el
  // webhook la guardó en `payments.last4digits`). null = no la mostramos.
  const [rebillCardLast4, setRebillCardLast4] = useState<string | null>(null);
  // Imágenes restantes actuales de la empresa: el confirm de cambio de plan
  // las usa para mostrar la suma exacta (restantes + incremento del plan).
  const [currentImagesLeft, setCurrentImagesLeft] = useState<number | null>(
    null,
  );
  const [paymentInfoResolved, setPaymentInfoResolved] =
    useState<boolean>(false);
  /**
   * El regalo que le espera a quien llegó por un link de referido
   * (ADR-2026-09-01-referidos-reglas-de-pago). `null` = no vino por un referido,
   * o ya cobró, o la consulta falló: los tres se dibujan igual (sin cartel), así
   * que no hace falta distinguirlos.
   *
   * Va acá y no en la pantalla de referidos porque el premio se acredita contra
   * el PAGO: éste es el único momento del recorrido en que decirlo cambia una
   * decisión. El servidor deja de devolverlo apenas se acredita, así que el
   * cartel no reaparece después de pagar sin que el front tenga que recordarlo.
   */
  const [invitacion, setInvitacion] = useState<MiInvitacionReferido | null>(
    null,
  );
  /**
   * ¿Esta cuenta ya fue cliente pago alguna vez?
   * (`company_profiles.paid_plan_start_date`). Se lee acá, de la base, y NO del
   * `location.state`: es el dato que decide si corresponde el cupón, y el estado
   * de navegación lo pone quien abre la pantalla (tres botones hoy, más la URL
   * directa y el back del navegador). Sale de la MISMA consulta que la puerta de
   * Stripe, así que no agrega un request al camino crítico del cobro.
   *
   * Arranca en `true` —"ya fue cliente", o sea SIN descuento— porque es el default
   * que no promete nada: nada de la UI de pago se pinta hasta que `puerta` sea
   * `'ok'` (y para entonces esto ya resolvió), y si la consulta falla la pantalla
   * corta en `'error'` sin cobrar. Ver ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis.
   */
  const [yaFueClientePago, setYaFueClientePago] = useState<boolean>(true);
  /**
   * Puerta de entrada al cobro — ADR-2026-07-31-checkout-cierra-puerta-clientes-stripe.
   *
   * Un cliente de Stripe con suscripción ACTIVA no puede llegar acá: una Checkout
   * Session nueva crea una SEGUNDA suscripción y la vieja sigue cobrando (nada la
   * cancela, el webhook sólo pisa `stripe_subscription_id`). Ver ADR-661.
   *
   * La verificación vive en ESTA pantalla y no sólo en los botones que la abren
   * porque los botones son tres y ninguno cubre la URL directa, un bookmark ni el
   * back del navegador. Acá es el único punto por el que pasan todos.
   *
   *  - `resolviendo` → loader; NO se dibuja nada de pago.
   *  - `stripe`      → se va al Billing Portal (el loader queda mientras navega).
   *  - `error`       → **falla cerrado**: sin saber la plataforma no se cobra.
   *  - `ok`          → checkout normal.
   */
  const [puerta, setPuerta] = useState<
    "resolviendo" | "ok" | "stripe" | "error"
  >("resolviendo");
  // Contexto para el checkout embebido de Rebill (empresa/usuario/prefill): lo
  // resuelve la MISMA consulta de la puerta, y se le pasa al componente para que
  // no repita `getUser` + `profiles` en el camino crítico del cobro.
  // Ver ADR-2026-08-24-checkout-rebill-carga-silenciosa.
  const [rebillCtx, setRebillCtx] = useState<RebillPrefetchedCtx | null>(null);
  // Precarga del SDK de Rebill (solo AR): los chunks se bajan en paralelo con la
  // puerta en vez de esperar a que `RebillCardPayment` monte. El catch vacío es
  // a propósito: si falla acá, `RebillCardPayment` vuelve a llamar y maneja el
  // error (`ensureRebillSDK` resetea su promesa al fallar, así que reintenta).
  useEffect(() => {
    if (isArgentina) void ensureRebillSDK().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Fallback: el one-click pidió cargar tarjeta (needs_checkout / declined) →
  // pasamos al checkout embebido aunque sea cliente Rebill.
  const [forceCheckout, setForceCheckout] = useState<boolean>(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        if (mounted) setUserId(user.id);
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company, name, name_short, name_sex_male")
          .eq("id", user.id)
          .single();
        if (mounted && profile) {
          setWelcomeInfo({
            name:
              ((profile as any).name_short as string | null)?.trim() ||
              ((profile as any).name as string | null)?.trim() ||
              "",
            sexMale: (profile as any).name_sex_male ?? null,
          });
        }
        const idCompany = (profile as any)?.id_company as string | null;
        // Contexto de Rebill: sale de lo que ya consultamos (sin requests extra).
        const fullName = ((profile as any)?.name as string | null)?.trim();
        if (mounted) {
          setRebillCtx({
            companyCtx: idCompany ? { idCompany, userId: user.id } : null,
            customerPrefill:
              user.email || fullName
                ? {
                    ...(user.email ? { email: user.email } : {}),
                    ...(fullName ? { fullName } : {}),
                  }
                : null,
          });
        }
        if (idCompany) {
          const { data: company, error: errorEmpresa } = await supabase
            .from("company_profiles")
            .select(
              // `paid_plan_start_date`: ¿ya fue cliente pago alguna vez? Decide si
              // corresponde el cupón (ver `yaFueClientePago` más abajo).
              "plataforma_pago, plan, rebill_customer_id, imagenes_restantes:imagenes_disponibles, paid_plan_start_date",
            )
            .eq("id_company", idCompany)
            .single();
          // `.single()` no tira: sin este chequeo un fallo dejaba `company` en
          // `null` y el usuario entraba al cobro como si fuera cliente nuevo.
          if (errorEmpresa || !company) throw errorEmpresa ?? new Error("sin empresa");
          const plataforma = (company as any).plataforma_pago as string | null;
          const planActual = (company as any).plan as string | null;
          if (plataforma === "stripe" && planActual && planActual !== "gratis") {
            if (mounted) setPuerta("stripe");
            // No se resuelve `paymentInfoResolved`: el loader se queda puesto
            // mientras el browser se va al portal.
            window.location.href = await fetchStripeBillingPortalUrl(user.id);
            return;
          }
          if (mounted) {
            setYaFueClientePago(!!(company as any).paid_plan_start_date);
          }
          const esClienteRebill =
            (company as any).plataforma_pago === "rebill" &&
            !!(company as any).rebill_customer_id;
          if (mounted && company) {
            setIsRebillCustomer(esClienteRebill);
            const left = (company as any).imagenes_restantes;
            if (typeof left === "number") setCurrentImagesLeft(left);
          }
          // ── ¿La tarjeta guardada es COBRABLE? (ADR-2026-08-07-billing-status-decide-el-one-click) ──
          // Tener `rebill_customer_id` sólo dice que es cliente de Rebill, NO que se
          // le pueda cobrar por API: Rebill exige documento (DNI/CUIT) y teléfono
          // para cobrar con `cardId` en AR, y el checkout embebido pide el DNI por
          // pago sin dejarlo en el customer. Sin ese chequeo, a quien le falta el
          // documento le mostrábamos el one-click, Rebill lo rechazaba con
          // `required_customer_document` y quedaba SIN NINGUNA forma de pagar: el
          // error cae en la pantalla "no pudimos confirmar", que esconde a propósito
          // el botón de usar otra tarjeta para no duplicar un cobro que quizá salió.
          // Le pasó a una clienta real el 2026-08-07 (20 cobros rechazados, 0 pagos).
          //
          // `forceCheckout` es el mismo interruptor que ya usa el fallback de
          // `RebillChangePlanConfirm`, así que el checkout embebido —que SÍ pide el
          // DNI— se muestra por el camino de siempre.
          //
          // Va ANTES de `setPaymentInfoResolved(true)` a propósito: si se resolviera
          // después, el one-click alcanzaría a pintarse y se cambiaría solo en la
          // cara del cliente.
          // Con el one-click apagado este chequeo no decide nada (su único efecto es
          // `forceCheckout`, que solo pesa cuando el one-click puede mostrarse), así
          // que nos ahorramos un request de hasta 6s en el camino crítico del cobro.
          if (ONE_CLICK_HABILITADO && esClienteRebill) {
            const estado = await fetchRebillBillingStatus(user.id);
            // `null` = no sabemos (red/timeout/5xx). Nos quedamos con el
            // comportamiento de siempre: mandar a todos al checkout embebido ante
            // una duda crearía suscripciones nuevas de más, y la baja de la vieja
            // todavía no está verificada (ADR-2026-08-07-baja-de-sub-vieja-sin-verificar).
            if (mounted && estado?.aplica && estado.puede_cobro_directo === false) {
              setForceCheckout(true);
            }
          }
          // Terminación de la tarjeta: último pago Rebill con `last4digits`.
          const { data: lastPayment } = await supabase
            .from("payments")
            .select("last4digits")
            .eq("id_company", idCompany)
            .eq("plataforma", "rebill")
            .not("last4digits", "is", null)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          const last4 = (lastPayment as any)?.last4digits as string | null;
          if (mounted && last4) setRebillCardLast4(last4);
        }
        if (mounted) setPuerta("ok");
      } catch {
        // Falla cerrado: sin poder confirmar la plataforma no se puede descartar
        // que sea un cliente de Stripe, y dejarlo pasar duplicaría su suscripción.
        if (mounted) setPuerta("error");
      } finally {
        if (mounted) setPaymentInfoResolved(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Coupon state
  const [showCouponInput, setShowCouponInput] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<string>("");
  const [couponLoading, setCouponLoading] = useState<boolean>(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    id: string;
    codigo: string;
    multiplicador: number;
    es_invisible?: boolean;
  } | null>(initialCoupon || null);

  // ¿Ya sabemos si hay cupón o no? El one-click NO se muestra hasta que esto sea
  // true: la consulta es async, y confirmar antes de que resuelva mandaría el
  // cobro SIN cupón —o sea, a precio de lista— aunque el catálogo ya le haya
  // mostrado el precio con descuento. Ver ADR-2026-08-07-cupon-en-el-one-click.
  // El checkout embebido TAMBIÉN espera esto, por otra razón: si monta con
  // `couponCode` null y el cupón resuelve después, el cambio de prop destruye y
  // recrea el <rebill-checkout> (toda la init de Rebill de nuevo, campos que
  // "se recargan" solos). Ver ADR-2026-08-24-checkout-rebill-carga-silenciosa.
  const [couponResolved, setCouponResolved] = useState<boolean>(!!initialCoupon);

  // Auto-load Hot Sale coupon if no coupon was passed from Paywall (todos los países).
  // El cupón se sigue aplicando — el branding visual lo manejamos desde Paywall.
  useEffect(() => {
    if (appliedCoupon) {
      setCouponResolved(true);
      return;
    }
    const loadHotSaleCoupon = async () => {
      try {
        const { data, error: dbError } = await supabase
          .from("cupones")
          .select(
            "id, codigo, multiplicador, activo, usos_maximos, usos_actuales, fecha_expiracion, es_invisible",
          )
          .eq("id", "9e0abd0d-5e0a-4f84-8036-b425f7176207")
          .single();
        if (!dbError && data && data.activo) {
          const expired =
            data.fecha_expiracion &&
            new Date(data.fecha_expiracion) < new Date();
          const maxedOut =
            data.usos_maximos !== null &&
            data.usos_actuales >= data.usos_maximos;
          if (!expired && !maxedOut) {
            setAppliedCoupon({
              id: data.id,
              codigo: data.codigo,
              multiplicador: data.multiplicador,
              es_invisible: true,
            });
          }
        }
      } catch {
      } finally {
        // Resuelto = ya sabemos la respuesta, haya cupón o no. Un fallo también
        // cuenta: es "no hay descuento", o sea precio de lista, que es lo que el
        // catálogo muestra cuando la misma consulta le falla a él (falla cerrado).
        setCouponResolved(true);
      }
    };
    loadHotSaleCoupon();
  }, [appliedCoupon]);

  const selectedPlan = plans.find((p) => p.id === planId);

  const getPlanPrice = (id: string, arsPrice: number) =>
    getPlanPriceForPais(id, arsPrice, pais);

  const formatPrice = (amount: number) => formatPriceForPais(amount, pais);

  /* Best-effort y sin bloquear nada: si tarda o falla, el checkout se dibuja
     igual y sin cartel. Un regalo no puede demorar un pago. */
  useEffect(() => {
    let vivo = true;
    miInvitacionReferido().then((info) => {
      if (vivo) setInvitacion(info);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Redirect back if no plan selected
  useEffect(() => {
    if (!planId || !selectedPlan) {
      navigate("/subscription", { replace: true });
    }
  }, [planId, selectedPlan, navigate]);


  // When user returns from payment provider (browser back), check if plan changed → redirect to success
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      // Only check if we were in a processing state (user was redirected to payment)
      if (!processing) return;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!prof?.id_company) return;
        const { data: company } = await supabase
          .from("company_profiles")
          .select("plan")
          .eq("id_company", prof.id_company)
          .single();
        if (company?.plan && company.plan === planId) {
          // User successfully subscribed to the plan they were buying
          navigate("/subscription/success", { replace: true });
          return;
        }
      } catch {}
      // If plan didn't change, just reset processing so user isn't stuck on spinner
      setProcessing(false);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [processing, planId, navigate]);

  // Also handle pageshow event (bfcache restoration on mobile Safari)
  useEffect(() => {
    const handlePageShow = async (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (!processing) return;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!prof?.id_company) return;
        const { data: company } = await supabase
          .from("company_profiles")
          .select("plan")
          .eq("id_company", prof.id_company)
          .single();
        if (company?.plan && company.plan === planId) {
          navigate("/subscription/success", { replace: true });
          return;
        }
      } catch {}
      setProcessing(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [processing, planId, navigate]);

  // Fire-and-forget: notify backend that user reached checkout
  const checkoutNotifiedRef = useRef(false);
  useEffect(() => {
    if (checkoutNotifiedRef.current) return;
    if (!planId || !selectedPlan) return;
    checkoutNotifiedRef.current = true;
    // Meta: se abrió el checkout con un plan elegido. Una vez por plan y carga.
    trackMeta(
      "InitiateCheckout",
      parametrosDePlan(planId, getPlanPrice(selectedPlan.id, selectedPlan.price), currencyCode),
      planId,
    );
    // Embudo: marca `vio_precios` en la empresa y avisa a Brevo
    // (PASO_POR_CHECKOUT + evento checkout_started). El usuario sale del JWT.
    atlasBackendFetch("/v1/tracking/checkout", {
      plan_selected: planId,
      price: getPlanPrice(selectedPlan.id, selectedPlan.price),
      currency: currencyCode,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, selectedPlan]);

  const handleValidateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponError("Ingresá un código de cupón");
      return;
    }
    try {
      setCouponLoading(true);
      setCouponError(null);
      const { data, error: dbError } = await supabase
        .from("cupones")
        .select(
          "id, codigo, multiplicador, activo, usos_maximos, usos_actuales, fecha_expiracion, es_invisible",
        )
        .eq("codigo", code)
        .single();
      if (dbError || !data) {
        setCouponError("Cupón no encontrado");
        return;
      }
      if (!data.activo) {
        setCouponError("Este cupón ya no está activo");
        return;
      }
      if (
        data.usos_maximos !== null &&
        data.usos_actuales >= data.usos_maximos
      ) {
        setCouponError("Este cupón ya alcanzó su límite de usos");
        return;
      }
      if (
        data.fecha_expiracion &&
        new Date(data.fecha_expiracion) < new Date()
      ) {
        setCouponError("Este cupón ha expirado");
        return;
      }
      setAppliedCoupon({
        id: data.id,
        codigo: data.codigo,
        multiplicador: data.multiplicador,
        es_invisible: !!data.es_invisible,
      });
      setShowCouponInput(false);
      setCouponCode("");
    } catch {
      setCouponError("Error al validar el cupón");
    } finally {
      setCouponLoading(false);
    }
  };

  // Checkout para Stripe / resto de países (redirect). Argentina usa el flujo
  // in-app con el checkout embebido de Rebill (RebillCardPayment), no este handler.
  const handleCheckout = async () => {
    setError(null);

    try {
      setProcessing(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no encontrado");

      // Redirect to the correct provider (Stripe / resto de países)
      const url = await fetchStripeCheckoutUrl({
        user_id: user.id,
        plan: planId!,
        ...(appliedCoupon &&
        planId &&
        cambioDePlanAdmiteDescuento(currentPlan, planId, yaFueClientePago)
          ? { con_descuento: true, cupon_id: appliedCoupon.id }
          : {}),
      });
      // Meta: el usuario pasó a cargar la tarjeta. La página de Stripe es de ellos y
      // ahí la app no ve nada, así que es lo último que se puede emitir del exterior.
      // `fbq` encola el envío y el redirect no lo espera; es best-effort a propósito.
      trackMeta(
        "AddPaymentInfo",
        parametrosDePlan(planId!, getPlanPrice(planId!, selectedPlan?.price ?? 0), currencyCode),
      );
      window.location.href = url;
    } catch (err: any) {
      console.error("Error in checkout:", err);
      setError(err.message || "Error al procesar el pago");
      setProcessing(false);
    }
  };

  if (!selectedPlan) return null;

  const price = getPlanPrice(selectedPlan.id, selectedPlan.price);
  // ¿Este cambio de plan puede llevar descuento? Depende del par (plan actual →
  // plan destino), no de si hay un cupón cargado — por eso se separa de
  // `hasDiscount`, que decide el precio mostrado y el `couponCode` que viaja a
  // `RebillCardPayment`. La regla vive en
  // `lib/checkoutPlans`, compartida con el catálogo: Básico nunca lleva promo, el
  // alta de quien nunca pagó sí, a quien ya paga solo se la lleva Básico → Pro, y
  // un ex cliente que volvió a `gratis` no la recupera
  // (ADR-2026-08-18-descuento-de-upgrade-solo-basico-a-pro y
  // ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis).
  const planAdmiteCupon = cambioDePlanAdmiteDescuento(
    currentPlan,
    selectedPlan.id,
    yaFueClientePago,
  );
  const hasDiscount = appliedCoupon && planAdmiteCupon;
  const discountedPrice = hasDiscount
    ? Math.floor(price * appliedCoupon.multiplicador)
    : price;

  // ── Qué pasa con tus imágenes al cambiar de plan ────────────────────────────
  // Este detalle vivía SOLO en el one-click (`RebillChangePlanConfirm`), así que al
  // apagarlo (ADR-2026-08-07-apagamos-el-one-click) el cliente que sube de plan se
  // quedaba sin la única explicación de que los créditos que le sobran NO se
  // pierden: el webhook SUMA el incremento del plan destino a las imágenes
  // restantes, y recién la próxima renovación resetea al cupo mensual.
  //
  // Solo para quien YA está en un plan pago y sube: a alguien que entra desde
  // gratis "no perdés lo que ya pagaste" no le dice nada. Si se entró a /checkout
  // sin `currentPlan` en el state, queda en 'gratis' y la nota no se muestra —
  // mejor no afirmar nada que prometer una suma que no podemos calcular.
  const esUpgradeDePago = isUpgrade && currentPlan !== "gratis";
  const planIncrement = planId
    ? (PLAN_UPGRADE_INCREMENTS[planId] as number | undefined)
    : undefined;
  const planDestino = planId ? getPlanById(planId) : undefined;
  const fmtNum = (n: number) => n.toLocaleString("es-AR");
  // `containerClass` porque los dos lugares donde va tienen fondos distintos: el
  // panel izquierdo del desktop es gris (#DDDFE6) y un borde neutral-200 ahí no se
  // ve, así que usa una tarjeta blanca translúcida; en mobile va sobre el fondo
  // claro y alcanza con el borde. Radio 22 = 8 del ícono + 14 del padding.
  const notaImagenes = (containerClass: string) =>
    !esUpgradeDePago ? null : (
      <div
        className={`flex items-start gap-3 p-3.5 rounded-[22px] ${containerClass}`}
      >
        <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
          <ImagePlus className="w-4 h-4 text-brand-primary" />
        </div>
        <div>
          <p className="text-[14px] font-medium text-brand-black/90">
            No perdés lo que ya pagaste
          </p>
          <p className="text-[13px] text-neutral-500 leading-snug">
            {planIncrement != null && currentImagesLeft != null
              ? `Se sumarán ${fmtNum(planIncrement)} imágenes de este nuevo plan a las ${fmtNum(currentImagesLeft)} que te quedan: pasás a tener ${fmtNum(currentImagesLeft + planIncrement)} disponibles ya mismo.`
              : "Las imágenes de este nuevo plan se suman a las que todavía te quedan."}
            {planDestino
              ? ` A partir de la próxima fecha de renovación pasarás a tener ${fmtNum(planDestino.max_fotos_mes)} imágenes de forma mensual.`
              : ""}
          </p>
        </div>
      </div>
    );

  // Pago AR: el checkout embebido de Rebill maneja tokenización + cobro. En
  // éxito reusamos la pantalla de éxito; en error mostramos el mensaje inline.
  // La activación del plan y la cancelación de la sub vieja las hace el backend
  // vía webhook (con la metadata id_company que manda el componente).
  const handleRebillSuccess = (_result: RebillCheckoutResult) => {
    // Diseño final del éxito AR: nos quedamos en esta pantalla — Rebill muestra su
    // "¡Pago exitoso!" en el panel del form y el panel izquierdo/top pasa a la
    // bienvenida con el botón "Continuar" (que rutea al home según grupo).
    setPaymentSuccess(true);
    // En mobile el recibo de Rebill es largo y el user queda scrolleado abajo →
    // subimos para que la bienvenida (arriba de todo) quede a la vista. El
    // timeout deja que React pinte el estado de éxito antes de scrollear.
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };
  const handleRebillError = (message: string) => {
    setCardError(message || "No pudimos procesar la tarjeta. Probá con otra.");
  };

  // Bienvenida post-pago: "{nombre}, bienvenido/a a Atlas".
  const welcomeVerb =
    welcomeInfo?.sexMale === true
      ? "bienvenido"
      : welcomeInfo?.sexMale === false
        ? "bienvenida"
        : "bienvenido/a";
  const welcomeHeading = welcomeInfo?.name
    ? `${welcomeInfo.name}, ${welcomeVerb} a Atlas`
    : `${welcomeVerb.charAt(0).toUpperCase()}${welcomeVerb.slice(1)} a Atlas`;
  const welcomeSubtitle = "Te damos la bienvenida a la revolución de la IA";
  // Destino del botón "Continuar": misma lógica que el landing de `/` — solo
  // Grupo B (no-dev) va a Interfaz V2; dev y Grupo A al flujo clásico.
  const homePath = entryStudioPath(userGroupDevTester, userGroupGrupo);

  // "Continuar" del panel de éxito: los argentinos (⇒ Rebill, porque el panel de
  // éxito solo se prende tras un pago Rebill) pasan por la pantalla de oferta de
  // facturación (/facturacion-oferta) antes de entrar al estudio; el resto va
  // directo al home. Ver ADR-2026-08-13-facturacion-pantalla-post-pago-ar.
  const handleContinuarExito = () => {
    // La decisión (Ultra → capacitación; AR → oferta de facturación; resto → estudio)
    // vive en `destinoPostPago`, compartida con el alta por link, que tenía su propia
    // copia y se había quedado sin la oferta de facturación.
    // Ver ADR-2026-09-22-el-destino-post-pago-es-uno-solo.
    const destino = destinoPostPago({
      planId,
      isArgentina,
      home: homePath,
    });
    navigate(destino.ruta, { replace: true, ...(destino.state ? { state: destino.state } : {}) });
  };

  // Área de pago AR (mobile + desktop comparten esta decisión):
  //  - cliente que YA está en Rebill (tarjeta guardada) → "Confirmar cambio"
  //    one-click (salvo que un fallback nos haya mandado al checkout);
  //  - resto (nuevo / migración MP→Rebill) → checkout embebido.
  const showOneClick =
    ONE_CLICK_HABILITADO && isRebillCustomer && !forceCheckout && !!userId;
  // El checkout embebido (alto) va top-aligned; el one-click / loader / éxito
  // (contenido corto) se centra vertical en desktop.
  const arShowsCheckout = isArgentina && paymentInfoResolved && !showOneClick;
  const renderArPayment = () => {
    if (!paymentInfoResolved) {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      );
    }
    if (showOneClick) {
      // El cupón se consulta async. Mientras no sepamos si hay descuento, seguimos
      // en el loader — NO en el checkout embebido: cambiar de UI acá haría que la
      // pantalla se transforme sola en la cara del cliente. Confirmar antes de que
      // resuelva mandaría el cobro a precio de lista con el catálogo mostrando el
      // precio con descuento, que es justo el bug que esto arregla.
      if (!couponResolved) {
        return (
          <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        );
      }
      return (
        // El cupón viaja como `cuponId`: el server lo valida contra `public.cupones`
        // y calcula el monto (ver ADR-2026-08-07-cupon-en-el-one-click, que supersede
        // el ADR-114 en esto). `priceLabel` sigue SIN pasarse a propósito: el
        // descuento recién se confirma en la respuesta del cobro (`couponApplied`),
        // así que hasta que el server esté verificado en producción no prometemos un
        // número en el botón. El monto real se muestra en la pantalla de éxito.
        <RebillChangePlanConfirm
          userId={userId!}
          plan={planId!}
          cuponId={hasDiscount ? (appliedCoupon?.id ?? null) : null}
          cardLast4={rebillCardLast4}
          currentImagesLeft={currentImagesLeft}
          onSuccess={(r) =>
            handleRebillSuccess({ subscriptionId: r.subscriptionId })
          }
          onNeedsCheckout={() => setForceCheckout(true)}
        />
      );
    }
    // Mismo loader que arriba: hasta no saber si hay cupón, no se monta el
    // checkout — un cambio posterior de `couponCode` lo destruiría y recrearía
    // (toda la init de Rebill de nuevo). En la práctica casi nunca se ve: la
    // consulta del cupón (1 request) le suele ganar a la puerta (4 en serie).
    if (!couponResolved) {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      );
    }
    return (
      <RebillCardPayment
        planId={planId!}
        couponCode={hasDiscount ? (appliedCoupon?.codigo ?? null) : null}
        prefetchedCtx={rebillCtx ?? undefined}
        onSuccess={handleRebillSuccess}
        onError={handleRebillError}
      />
    );
  };

  // ── Puerta de Stripe (ADR-2026-07-31-checkout-cierra-puerta-clientes-stripe) ──
  // Va ANTES de cualquier UI de pago: mientras no esté confirmado que el usuario
  // puede cobrarse acá, no se dibuja ni el resumen del plan ni el formulario.
  if (puerta !== "ok") {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        {puerta === "error" ? (
          <div className="max-w-md text-center">
            <p className="text-[15px] text-brand-black/90">
              No pudimos verificar los datos de tu suscripción, así que no
              seguimos con el cobro para no duplicarla.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
            >
              Reintentar
            </button>
          </div>
        ) : (
          // `resolviendo` y `stripe` comparten el loader: en el segundo el browser
          // ya se está yendo al portal y un cartel alcanzaría a verse como un flash.
          <div className="flex items-center gap-2 text-[15px] text-neutral-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-surface-card md:bg-surface-bg">
      {/* ═══════════ MOBILE LAYOUT (Stripe-style) ═══════════ */}
      <div
        className={`md:hidden flex flex-col min-h-screen ${isArgentina ? "pb-10" : "pb-[140px]"}`}
      >
        {/* Mobile header. En PWA standalone con status bar `black-translucent`
            el contenido se mete debajo de la barra de estado → sumamos
            `env(safe-area-inset-top)` al padding-top para que el logo no quede
            pisado por el reloj/íconos. */}
        <div
          className="px-5 pb-2 flex items-center justify-between"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}
        >
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate("/subscription", { replace: true })}
              className="text-neutral-500 hover:text-neutral-700 transition-colors"
              aria-label="Volver"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-7 h-7 rounded-full bg-brand-black flex items-center justify-center shrink-0">
              <img
                src="/images/estudio-atlas-logo-solo-blanco.svg"
                alt=""
                className="w-4 h-4"
              />
            </div>
            <span className="text-[14px] font-semibold text-brand-black/90">
              Estudio Atlas
            </span>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-neutral-400">
            <Lock className="w-3 h-3" />
            <span>Pago seguro</span>
          </div>
        </div>

        {error && (
          <div className="mx-5 mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Plan summary / bienvenida post-pago (AR) */}
        {paymentSuccess ? (
          <div className="px-5 pt-8 pb-6 border-b border-neutral-200 text-center">
            <h1 className="text-[22px] font-bold text-brand-black/90 tracking-tight leading-tight">
              {welcomeHeading}
            </h1>
            <p className="text-[13px] text-neutral-500 mt-2">
              {welcomeSubtitle}
            </p>
            <button
              type="button"
              onClick={handleContinuarExito}
              className="mt-5 inline-flex items-center justify-center py-2.5 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Continuar
            </button>
          </div>
        ) : (
        <div className="px-5 pt-7 pb-5 border-b border-neutral-200 text-center">
          <p className="text-[13px] text-neutral-500">
            Plan {selectedPlan.name}
          </p>
          {hasDiscount ? (
            <div className="mt-1">
              <p className="text-[28px] font-bold text-brand-black/90 tracking-tight leading-tight">
                {formatPrice(discountedPrice)}
                <span className="text-[13px] font-normal text-neutral-500 ml-1">
                  el primer mes
                </span>
              </p>
              <p className="text-[13px] text-neutral-600 mt-0.5">
                Después {formatPrice(price)} por mes
              </p>
            </div>
          ) : (
            <p className="text-[28px] font-bold text-brand-black/90 tracking-tight leading-tight mt-1">
              {formatPrice(price)}
              <span className="text-[13px] font-normal text-neutral-400 ml-1">
                / mes
              </span>
            </p>
          )}

          {/* Expandable features */}
          <button
            type="button"
            onClick={() => setShowFeatures(!showFeatures)}
            className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
          >
            Ver qué incluye
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${showFeatures ? "rotate-180" : ""}`}
            />
          </button>

          {showFeatures && (
            <ul className="mt-3 space-y-2 text-left">
              {selectedPlan.features.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-center text-[13px] text-neutral-600"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-primary mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        {/* Payment section */}
        <div className="px-5 pt-3 space-y-5">
          {/* ═══ El regalo del amigo que invitó ═══
              Banner inline y no modal: acá el usuario ya decidió pagar y está a
              un clic del botón. Un diálogo encima le agrega un paso para cerrar
              justo antes de la conversión, y —peor— puede tapar el formulario en
              mobile. Como franja se lee de paso y no estorba.

              Va ARRIBA de `notaImagenes` porque los dos hablan de créditos: si
              el regalo apareciera después, el usuario suma mal la cuenta que
              acaba de leer.

              No se muestra si el cupón deja el precio en 0: con importe cero el
              pago no califica y el premio no se acredita
              (ADR-2026-09-01-referidos-reglas-de-pago). Prometerlo igual sería
              exactamente el reclamo que este cartel existe para evitar. */}
          {invitacion && !paymentSuccess && discountedPrice > 0 && (
            <div className="flex items-start gap-2.5 p-3 rounded-md bg-brand-primary/5 border border-brand-primary/20">
              <Gift className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
              <p className="text-[13px] leading-snug text-brand-black/90">
                {/* "un amigo" y no el nombre cuando viene `null`: quien invitó
                    puede no haber cargado nunca su nombre, y el cartel no puede
                    decir "null te regala" ni dejar un hueco. */}
                <strong>
                  {invitacion.nombre ?? "Un amigo"} te regala{" "}
                  {fmtNum(invitacion.creditos)} créditos
                </strong>
                <span className="block text-neutral-500">
                  Se acreditan en cuanto actives tu plan.
                </span>
              </p>
            </div>
          )}

          {notaImagenes("border border-neutral-200/80")}

          {/* Método de pago — solo Stripe (AR usa el formulario de tarjeta) */}
          {!isArgentina && (
            <div>
              <p className="text-[13px] font-medium text-brand-black/90 mb-1.5">
                Método de pago
              </p>
              <div className="flex items-center justify-center gap-2.5 p-3 rounded-md bg-brand-primary/5 border border-brand-primary/20">
                <img
                  src={providerLogoUrl}
                  alt={providerName}
                  className="h-4 w-auto"
                />
              </div>
            </div>
          )}


          {/* Stripe info — only for non-Argentina */}
          {!isArgentina && (
            <div className="mt-1">
              <p className="text-[13px] text-neutral-500">
                Al hacer clic en "Activar mi Estudio Atlas", serás redirigido a
                Stripe para completar tu suscripción de manera segura.
              </p>
            </div>
          )}

          {/* Coupon section — mobile (todos los países) */}
          {(
            <div className="mt-4">
              {appliedCoupon &&
              appliedCoupon.es_invisible ? null : appliedCoupon ? (
                <div className="flex items-center justify-between p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-brand-primary" />
                    <span className="text-[13px] text-brand-black/90">
                      Cupón <strong>{appliedCoupon.codigo}</strong> —{" "}
                      {Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAppliedCoupon(null)}
                    className="text-[13px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
                  >
                    Quitar
                  </button>
                </div>
              ) : showCouponInput ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleValidateCoupon();
                      }}
                      placeholder="Código de cupón"
                      className="flex-1 h-10 px-3 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleValidateCoupon}
                      disabled={couponLoading}
                      className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center gap-1.5"
                    >
                      {couponLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Aplicar"
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCouponInput(false);
                        setCouponCode("");
                        setCouponError(null);
                      }}
                      className="text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-[13px] text-red-600 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {couponError}
                    </p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCouponInput(true)}
                  className="flex items-center gap-1.5 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
                >
                  <Tag className="w-3.5 h-3.5" />
                  Tengo un cupón
                </button>
              )}
            </div>
          )}

          {/* Tarjeta — Argentina (captura in-app). El Brick pide también el
              email (después de los datos de la tarjeta). Solo en viewport
              mobile para no montar dos Bricks (ver isDesktop). */}
          {isArgentina && !isDesktop && (
            <div>
              {cardError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{cardError}</span>
                </div>
              )}
              {renderArPayment()}
            </div>
          )}
        </div>
      </div>

      {/* Mobile sticky footer — solo Stripe (AR usa el botón "Pagar" del Brick) */}
      {!isArgentina && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 bg-surface-card border-t border-neutral-200 px-5 pt-3 z-40"
          style={{
            boxShadow: "0 -4px 20px rgba(15, 23, 42, 0.06)",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)",
          }}
        >
          <button
            onClick={handleCheckout}
            disabled={processing}
            className="w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm flex items-center justify-center transition-colors"
          >
            {processing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Activar mi Estudio Atlas"
            )}
          </button>
          <p className="text-[11px] text-neutral-400 text-center mt-2">
            Al continuar aceptás los{" "}
            <a
              href={providerTermsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary hover:underline"
            >
              términos
            </a>{" "}
            de {providerName}
          </p>
        </div>
      )}

      {/* ═══════════ DESKTOP LAYOUT ═══════════ */}
      <div className="hidden md:flex min-h-screen">
        {/* ─── Left half — fondo navbar, logo, precio grande, plan ─── */}
        <div className="w-1/2 bg-[#DDDFE6] flex flex-col px-10 lg:px-20 py-12">
          <div className="flex items-center gap-3 mb-16">
            <button
              onClick={() => navigate("/subscription", { replace: true })}
              className="text-neutral-500 hover:text-neutral-800 transition-colors"
              aria-label="Volver"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-brand-black flex items-center justify-center shrink-0">
                <img
                  src="/images/estudio-atlas-logo-solo-blanco.svg"
                  alt=""
                  className="w-5 h-5"
                />
              </div>
              <span className="text-[16px] font-semibold text-brand-black/90">
                Estudio Atlas
              </span>
            </div>
          </div>

          {paymentSuccess ? (
            <div className="max-w-[460px]">
              <h1 className="text-[40px] leading-[1.1] font-bold text-brand-black/90 tracking-tight">
                {welcomeHeading}
              </h1>
              <p className="text-[16px] text-neutral-500 mt-3">
                {welcomeSubtitle}
              </p>
              <button
                type="button"
                onClick={handleContinuarExito}
                className="mt-8 inline-flex items-center justify-center py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
              >
                Continuar
              </button>
            </div>
          ) : (
            <>
          <div className="max-w-[440px]">
            <p className="text-[15px] text-neutral-500 mb-1">
              Suscribite al Plan {selectedPlan.name}
            </p>
            <div className="flex items-baseline gap-2.5">
              <span className="text-[46px] leading-none font-bold text-brand-black/90 tracking-tight">
                {formatPrice(hasDiscount ? discountedPrice : price)}
              </span>
              <span className="text-[14px] text-neutral-500">
                {hasDiscount ? "el primer mes" : "por mes"}
              </span>
            </div>
            {hasDiscount && (
              <p className="text-[13px] text-neutral-600 mt-2">
                Después {formatPrice(price)} / mes
              </p>
            )}

            <ul className="mt-10 space-y-3">
              {selectedPlan.features.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-center text-[14px] text-neutral-600"
                >
                  <CheckCircle2 className="w-4 h-4 text-brand-primary mr-2.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            {/* El regalo del amigo que invitó — misma condición y mismo texto
                que en la columna mobile (ver el comentario de allá, que explica
                por qué es banner y por qué no va con el precio en 0). Está
                duplicado porque el checkout tiene dos layouts enteros y
                excluyentes —`md:hidden` y `hidden md:flex`—, no un layout
                responsive: lo que se agrega a uno no aparece en el otro. Si se
                toca uno, tocar el otro. */}
            {invitacion && !paymentSuccess && discountedPrice > 0 && (
              <div className="mt-8 max-w-[460px] flex items-start gap-2.5 p-3 rounded-md bg-white/60 border border-brand-primary/20">
                <Gift className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                <p className="text-[13px] leading-snug text-brand-black/90">
                  <strong>
                    {invitacion.nombre ?? "Un amigo"} te regala{" "}
                    {fmtNum(invitacion.creditos)} créditos
                  </strong>
                  <span className="block text-neutral-500">
                    Se acreditan en cuanto actives tu plan.
                  </span>
                </p>
              </div>
            )}

            {/* Qué pasa con las imágenes al subir de plan — debajo de las
                características, del lado del plan y no del formulario. */}
            {esUpgradeDePago && (
              <div className="mt-8 max-w-[460px]">
                {notaImagenes("bg-white/60")}
              </div>
            )}
          </div>

          <p className="mt-auto pt-10 text-[12px] text-neutral-400">
            Se renueva automáticamente cada mes. Cancelás cuando quieras.
          </p>
            </>
          )}
        </div>

        {/* ─── Right half — solo el formulario, sin borde ─── */}
        <div className={`w-1/2 bg-surface-card flex flex-col px-10 lg:px-20 ${arShowsCheckout ? "justify-start py-8" : "justify-center py-12"}`}>
          <div className={`w-full mx-auto ${isArgentina ? "max-w-[480px]" : "max-w-[440px]"}`}>
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Método de pago + info — solo Stripe */}
            {!isArgentina && (
              <div className="mb-5">
                <label className="block text-[13px] font-medium text-brand-black/90 mb-1.5">
                  Método de pago
                </label>
                <div className="flex items-center justify-center gap-2.5 p-3 rounded-md bg-brand-primary/5 border border-brand-primary/20">
                  <img
                    src={providerLogoUrl}
                    alt={providerName}
                    className="h-5 w-auto"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mt-3">
                  Al hacer clic en "Activar mi Estudio Atlas", serás redirigido a
                  Stripe para completar tu suscripción de manera segura.
                </p>
              </div>
            )}

            {/* Cupón */}
            <div className="mb-5">
              {appliedCoupon &&
              appliedCoupon.es_invisible ? null : appliedCoupon ? (
                <div className="flex items-center justify-between p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-brand-primary" />
                    <span className="text-[13px] text-brand-black/90">
                      Cupón <strong>{appliedCoupon.codigo}</strong> —{" "}
                      {Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAppliedCoupon(null)}
                    className="text-[13px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
                  >
                    Quitar
                  </button>
                </div>
              ) : showCouponInput ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleValidateCoupon();
                      }}
                      placeholder="Código de cupón"
                      className="flex-1 h-10 px-3 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleValidateCoupon}
                      disabled={couponLoading}
                      className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center gap-1.5"
                    >
                      {couponLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Aplicar"
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCouponInput(false);
                        setCouponCode("");
                        setCouponError(null);
                      }}
                      className="text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-[13px] text-red-600 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {couponError}
                    </p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCouponInput(true)}
                  className="flex items-center gap-1.5 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
                >
                  <Tag className="w-3.5 h-3.5" />
                  Tengo un cupón
                </button>
              )}
            </div>

            {/* AR: tarjeta in-app (el Brick pide también el email). */}
            {isArgentina && isDesktop && (
              <div>
                {cardError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{cardError}</span>
                  </div>
                )}
                {renderArPayment()}
              </div>
            )}
            {!isArgentina && (
              <button
                onClick={handleCheckout}
                disabled={processing}
                className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm flex items-center justify-center transition-colors"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Activar mi Estudio Atlas"
                )}
              </button>
            )}

            {!isArgentina && (
              <p className="text-[11px] text-neutral-400 text-center mt-4">
                Al continuar aceptás los{" "}
                <a
                  href={providerTermsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline"
                >
                  términos y condiciones
                </a>
                {` de ${providerName}`}
              </p>
            )}
            {!isArgentina && (
              <p className="text-[11px] text-neutral-400 text-center mt-3 flex items-center justify-center gap-1.5">
                <Lock className="w-3.5 h-3.5" />
                Pago 100% seguro · Cancelás cuando quieras
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mercado Pago Email Tutorial Modal */}
      {showMpEmailTutorial && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-[60] px-6 py-4">
          <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
            <button
              onClick={() => setShowMpEmailTutorial(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            {mpEmailTutorialStep === 1 ? (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail.png"
                    alt="Paso 1: Pantalla de inicio de Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-md"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mb-4">
                  Para conocer qué correo tienes asociado a tu cuenta de Mercado
                  Pago, entra a la aplicación y en la pantalla de inicio haz
                  click en la esquina superior izquierda.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setMpEmailTutorialStep(2)}
                    className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Siguiente
                  </button>
                  <button
                    onClick={() => setShowMpEmailTutorial(false)}
                    className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 text-[15px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail_2.png"
                    alt="Paso 2: Ver tu email en Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-md"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mb-4">
                  En esta pantalla podrás ver el correo electrónico asociado a
                  tu cuenta de Mercado Pago. Usa ese mismo correo al
                  suscribirte.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setShowMpEmailTutorial(false)}
                    className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Entendido
                  </button>
                  <button
                    onClick={() => setMpEmailTutorialStep(1)}
                    className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 text-[15px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
                  >
                    Volver
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Guard de dueño (spec 2026-09-11-invitaciones-equipo §4.3): un miembro no paga ni cambia el plan.
 *  Un guard acá cubre todos los CTAs de "mejorar plan" de la app sin tocar cada uno. */
export const Checkout = () => {
  const cuenta = useCuentaAtlas();
  if (cuenta.cargando) return null;
  if (!cuenta.esDueno) return <SoloElDueno />;
  return <CheckoutDelDueno />;
};

export default Checkout;
